/**
 * Recovery from a chunk that no longer exists.
 *
 * Every screen is its own chunk, fetched the first time it is opened. After a
 * deploy, a tab still running the previous build asks for chunk names that the
 * new deployment does not have. Cloudflare Pages then answers with index.html
 * (SPA fallback), so the import fails with a MIME-type error. The service worker
 * usually prevents this (it precaches every chunk and reloads on update), but not
 * on a first visit, in a private window or where service workers are disabled.
 *
 * The fix is one reload, which picks up the new build. The guard prevents a
 * reload loop when the chunk is missing for some other reason: the second
 * failure within the window surfaces to the ErrorBoundary instead.
 */

const RELOAD_KEY = 'quipwa.chunkReloadAt'
export const RELOAD_WINDOW_MS = 10_000

/**
 * The messages browsers use when a dynamic import cannot be fetched or
 * evaluated as a module (Chrome, Firefox, Safari), plus Vite's CSS preload
 * failure.
 */
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|Unable to preload CSS/i

export function isChunkLoadError(err: unknown): boolean {
  return err instanceof Error && CHUNK_ERROR.test(err.message)
}

interface ReloadDeps {
  storage?: Pick<Storage, 'getItem' | 'setItem'> | undefined
  now?: number | undefined
  reload?: (() => void) | undefined
}

/**
 * Reloads the page unless it already did so within RELOAD_WINDOW_MS.
 * Returns whether a reload was started.
 *
 * Without a working sessionStorage there is no way to notice a loop, so it
 * does not reload at all and the error is shown instead.
 */
export function reloadForNewBuild({ storage, now = Date.now(), reload = () => window.location.reload() }: ReloadDeps = {}): boolean {
  const store = storage ?? safeSessionStorage()
  if (!store) return false
  try {
    const last = Number(store.getItem(RELOAD_KEY)) || 0
    if (now - last < RELOAD_WINDOW_MS) return false
    store.setItem(RELOAD_KEY, String(now))
  } catch {
    return false
  }
  reload()
  return true
}

function safeSessionStorage(): Storage | undefined {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}
